/**
 * The narrow view of a data channel that the sharing protocol actually uses.
 *
 * `RTCDataChannel` is not something a test can construct, and a protocol that can only be
 * exercised by opening a real peer connection between two browsers is a protocol that does
 * not get exercised. So the conversation is written against this instead, and two of them
 * wired back to back stand in for a network.
 *
 * The methods are the ones that matter for moving a large file through a channel that can
 * be overrun: how much is queued, and how to wait until it is not.
 */

/** What crosses the channel: JSON control messages, and the file bytes themselves. */
export type Frame = string | ArrayBuffer;

export interface Wire {
  send(frame: Frame): void;
  /** Bytes accepted but not yet handed to the network. */
  buffered(): number;
  /** Resolves once the queue has drained enough to be worth writing to again. */
  drained(): Promise<void>;
  onFrame(handler: (frame: Frame) => void): void;
  onClosed(handler: () => void): void;
  close(): void;
}

/**
 * How much to write at a time.
 *
 * 16 KiB is the largest message every WebRTC implementation is obliged to accept. Larger
 * messages work between two of the same browser and fail between different ones, which is
 * the worst possible way for a limit to be wrong: it works in testing and fails for the
 * person whose laptop and headset disagree.
 */
export const CHUNK_BYTES = 16 * 1024;

/** Stop writing above this, and resume once the channel has drained to the low mark. */
const HIGH_WATER = 1024 * 1024;
const LOW_WATER = 256 * 1024;

/** Adapt a real data channel to the interface above. */
export function wireOf(channel: RTCDataChannel): Wire {
  channel.binaryType = 'arraybuffer';
  channel.bufferedAmountLowThreshold = LOW_WATER;

  return {
    send(frame) {
      // The overloads on `send` do not accept a union, though every arm is valid.
      if (typeof frame === 'string') channel.send(frame);
      else channel.send(frame);
    },
    buffered: () => channel.bufferedAmount,
    drained: () =>
      new Promise((resolve) => {
        if (channel.bufferedAmount <= LOW_WATER) {
          resolve();
          return;
        }
        channel.addEventListener('bufferedamountlow', () => {
          resolve();
        });
      }),
    onFrame(handler) {
      channel.addEventListener('message', (event: MessageEvent<unknown>) => {
        const { data } = event;
        if (typeof data === 'string' || data instanceof ArrayBuffer) handler(data);
      });
    },
    onClosed(handler) {
      channel.addEventListener('close', handler);
      channel.addEventListener('error', handler);
    },
    close: () => {
      channel.close();
    },
  };
}

/**
 * Write a blob across a wire in chunks, pausing when the channel falls behind.
 *
 * The file is sliced rather than read whole: a 200 MB model read into an `ArrayBuffer` and
 * then copied chunk by chunk is 400 MB of a headset's memory, and the headset is the
 * device least able to spare it.
 */
export async function sendBlob(
  wire: Wire,
  blob: Blob,
  onProgress: (bytes: number) => void,
): Promise<void> {
  for (let offset = 0; offset < blob.size; offset += CHUNK_BYTES) {
    // Without this the send queue grows without limit and the tab is killed for using
    // too much memory long before the transfer finishes.
    if (wire.buffered() > HIGH_WATER) await wire.drained();

    const end = Math.min(offset + CHUNK_BYTES, blob.size);
    wire.send(await blob.slice(offset, end).arrayBuffer());
    onProgress(end - offset);
  }
}
